import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBabyCarriage, IconArmchair, IconUsers, IconPhone, IconUsersGroup, IconDeviceSdCard, IconId } from '@tabler/icons-react';
import { formatDate, formatDayLong, sortPersons } from '@/lib/format-utils';
import { ServiceTitleStatic } from '@/components/services/service-title';
import {
    InventoryItem,
    Person,
    ServiceBooking,
    ServiceBookingPaxGroup,
    ServiceOrder,
    ServiceOrderBooking,
    ServiceOrderPaxGroup,
    ServiceOrderPerson,
} from '@turizi/shared';
import { localTenant } from '@/services/session-service';
import { getCapacityIcon } from './service-order-inventory-selection';
import { calculateOccupiedSlots, calculateUniquePaxCategories, resolveOrderBookingPaxGroups } from '@/lib/models-utils';
import { BookingPaxsSize, ShowPerson } from '@/components/snippets';


interface ServiceOrderManifestViewProps {
    order: ServiceOrder;
    showPaxList?: boolean;
    onlyFirstPax?: boolean;
    showPhone?: boolean;
}

interface ManifestBooking extends ServiceBooking {
    service_order_pax_groups?: ServiceOrderPaxGroup[];
    _service_order_booking?: ServiceOrderBooking;
    _original_booking?: ServiceBooking;
}


export default function ServiceOrderManifestView(
    {
        order,
        showPaxList = true,
        onlyFirstPax = false,
        showPhone = true,
    }: ServiceOrderManifestViewProps
) {
    const { t } = useTranslation();
    const tenant = localTenant();

    const categories: { key: 'infants' | 'children' | 'adults' | 'seniors' | 'guests'; bg: string; text: string }[] = [
        { key: 'infants', bg: 'bg-purple-100', text: 'text-purple-700' },
        { key: 'children', bg: 'bg-blue-100', text: 'text-blue-700' },
        { key: 'adults', bg: 'bg-slate-200', text: 'text-slate-700' },
        { key: 'seniors', bg: 'bg-orange-100', text: 'text-orange-700' },
        { key: 'guests', bg: 'bg-teal-100', text: 'text-teal-700' },
    ];

    const manifestUrl = `${window.location.origin}/service-orders/${order.id}/manifest`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(manifestUrl)}`;

    const normalBookings = useMemo<ManifestBooking[]>(() => {
        if (!order) return [];
        return (order.service_order_bookings || [])
            .map((link): ManifestBooking | null => {
                const b = link.service_booking;
                if (!b) return null;

                // Find which persons are assigned to this booking in this service order
                const assignedPersons: Person[] = (order.service_order_persons || [])
                    .filter((sop: ServiceOrderPerson) => sop.service_booking_id === b.id)
                    .map((sop: ServiceOrderPerson): Person | null => {
                        const p = sop.person_data || sop.person;
                        if (!p) return null;
                        return {
                            ...p,
                            ServiceBookingPerson: p.ServiceBookingPerson
                        };
                    })
                    .filter((p): p is Person => p !== null);

                const assignedPaxGroups: ServiceOrderPaxGroup[] = resolveOrderBookingPaxGroups(order, b);

                const fallbackGroupPersons: Person[] = assignedPaxGroups.flatMap((sog: ServiceOrderPaxGroup) => sog.pax_group?.persons || []);
                const finalPersons: Person[] = assignedPersons.length > 0 ? assignedPersons : fallbackGroupPersons;

                return {
                    ...b,
                    adults: link.adults,
                    children: link.children,
                    infants: link.infants,
                    seniors: link.seniors,
                    guests: link.guests,
                    persons: finalPersons,
                    service_order_pax_groups: assignedPaxGroups,
                    _service_order_booking: link,
                    _original_booking: b
                };
            })
            .filter((b): b is ManifestBooking => b !== null);
    }, [order]);

    const bookingsGroups = useMemo(() => {
        if (order.group_all_bookings !== false) {
            return normalBookings.length > 0 ? [normalBookings] : [];
        } else {
            const groupsMap = new Map<number, ManifestBooking[]>();
            normalBookings.forEach(booking => {
                const serviceId = booking.service_id || 0;
                if (!groupsMap.has(serviceId)) {
                    groupsMap.set(serviceId, []);
                }
                groupsMap.get(serviceId)!.push(booking);
            });
            return Array.from(groupsMap.values());
        }
    }, [normalBookings, order.group_all_bookings]);

    const orderBookings = useMemo(() => {
        if (!order) return [];
        return (order.service_bookings || []) as ServiceBooking[];
    }, [order]);

    const inventoryItems = useMemo(() => {
        if (!order) return [];
        const links = order.service_order_inventory_items || [];
        return links.map(link => link.inventory_item).filter(Boolean) as InventoryItem[];
    }, [order]);

    const totalOccupiedSlots = useMemo(() => {
        return calculateOccupiedSlots(order);
    }, [order]);

    return (
        <div className="space-y-1 print:p-0">
            {/* Header / ID, Logo, and QR Code */}
            <div className="flex justify-between items-center pb-1 mb-1">
                <div className="flex flex-col">
                    <span className="text-xxs font-bold text-gray-400 uppercase tracking-wider">
                        {t('service_order')}
                    </span>
                    <span className="text-xl font-extrabold text-slate-800">
                        #{order.id}
                    </span>
                </div>

                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest hidden sm:block">
                    <span className="font-semibold">{tenant?.settings?.store?.display_name || tenant?.name}</span>
                </div>

                <div className="flex items-center gap-3">
                    {tenant?.settings?.logo_url && (
                        <img
                            src={tenant.settings.logo_url}
                            alt="Logo"
                            className="h-9 object-contain"
                        />
                    )}
                    <img
                        src={qrUrl}
                        alt="QR Code"
                        className="w-12 h-12 object-contain"
                    />
                </div>
            </div>

            {/* Observation */}
            {order.observations && (
                <div className="p-1 bg-red-100 rounded-md text-xs w-fit">
                    {order.observations}
                </div>
            )}

            <div className='text-sm solid-box-border space-y-2'>
                {/* Inventory Items */}
                {
                    inventoryItems.map((item) => {
                        const CapacityIcon = getCapacityIcon(item);
                        const isOverCapacity = !!item.capacity && totalOccupiedSlots > item.capacity;
                        return (
                            <div key={item.id} className="flex items-center gap-1">
                                <div className="font-light">
                                    {item.full_path}:
                                </div>
                                <div className="font-bold">
                                    {item.name || 'no_name'}
                                </div>
                                <div className={`
                                    inline-flex items-center gap-0.5 text-md font-bold ml-1 border border-1 px-1 rounded-md
                                    ${isOverCapacity ? 'border-red-600 text-red-600' : 'border-green-600 text-green-600'}
                                `}>
                                    <CapacityIcon size={14} stroke={2} className="shrink-0" />
                                    <span>{totalOccupiedSlots} / {item.capacity || '-'}</span>
                                </div>
                                {item.notes && (
                                    <div className="text-gray-500">
                                        ({item.notes})
                                    </div>
                                )}
                            </div>
                        );
                    })
                }

                {/* Operational Services (Order Bookings) */}
                {orderBookings.map((booking) => (
                    <div key={booking.id} className="flex items-center gap-1">
                        <div className="font-light">
                            {booking.service_data?.name_public || booking.service_data?.name || ''}:
                        </div>
                        {booking.selected_provider?.name ? (
                            <div className="font-bold">
                                {booking.selected_provider.name}
                            </div>
                        ) : (
                            <div className="text-gray-400 italic">
                                {t('no_provider_assigned')}
                            </div>
                        )}
                        {booking.inventory_slots > 0 && (
                            <div className="inline-flex items-center gap-0.5 text-xs font-bold ml-1 solid-box-border-tinted">
                                <IconUsers size={11} stroke={1.5} className="shrink-0" />
                                <span>{booking.inventory_slots ?? '-'}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Services and Passengers */}
            {bookingsGroups.map((group, groupIdx) => {
                const firstBooking = group[0];
                const allAttrs = group.flatMap(b => b.self_attrs || []);
                const keyMap = new Map<string, { key: string; type: string; sort: number }>();
                allAttrs.forEach(attr => {
                    if (attr && attr.key && !keyMap.has(attr.key)) {
                        keyMap.set(attr.key, {
                            key: attr.key,
                            type: attr.type,
                            sort: attr.sort ?? 0
                        });
                    }
                });
                const uniqueAttrs = Array.from(keyMap.values()).sort((a, b) => a.sort - b.sort);

                // Sort bookings within the single group by start time
                const sortedBookings = [...group].sort((a, b) => {
                    const timeA = a.start_hour || '';
                    const timeB = b.start_hour || '';
                    return timeA.localeCompare(timeB);
                });

                const totalInfants = sortedBookings.reduce((acc, b) => acc + (b.infants || 0), 0);

                // this number seems wrong
                const groupOrderSlice: ServiceOrder = {
                    ...order,
                    service_order_bookings: group
                        .map(b => b._service_order_booking)
                        .filter((link): link is ServiceOrderBooking => Boolean(link)),
                    service_bookings: [],
                };
                const paxCategories = calculateUniquePaxCategories(groupOrderSlice);
                const totalSeats = paxCategories.adults + paxCategories.children + paxCategories.seniors + paxCategories.guests;

                return (
                    <div key={groupIdx} className="space-y-1 mt-4">
                        <div className="flex gap-4 text-sm items-center flex-wrap">
                            <div className="bg-black text-white px-2 py-1.5 font-bold rounded">
                                {formatDayLong(firstBooking.start_day)}
                            </div>
                            <div className="font-bold text-gray-800">
                                {ServiceTitleStatic(firstBooking?.service_data)}
                            </div>
                            <div className="h-fit text-sm bg-gray-100 text-gray-700 px-2 py-0.5 rounded flex items-center gap-1.5 shrink-0">
                                {totalInfants > 0 && (
                                    <>
                                        <span className="flex items-center gap-1">
                                            <IconBabyCarriage size={11} stroke={1.5} className="shrink-0 text-gray-400" />
                                            {totalInfants}
                                        </span>
                                        <span className="text-gray-300">&middot;</span>
                                    </>
                                )}
                                <span className="flex items-center gap-1 font-extrabold">
                                    <IconUsers size={11} stroke={1.5} className="shrink-0 text-gray-400" />
                                    {totalSeats}
                                </span>
                            </div>
                        </div>
                        <div className="border border-gray-200 rounded-md overflow-hidden">
                            <table className="w-full text-left bg-white">
                                <thead>
                                    <tr className="bg-gray-200 text-gray-400 text-xxs font-bold tracking-wider">
                                        <th className='py-2 px-2'>{t('time')}</th>
                                        {/* <th >{t('group')}</th> */}
                                        <th className='py-2 px-2'>{t('name')}</th>
                                        {categories.map(c => (
                                            <th key={c.key} className="number text-center py-1" style={{ width: '25px' }} title={t(c.key)}>
                                                <span className={`w-4 h-4 rounded-full ${c.bg} ${c.text} inline-flex items-center justify-center text-[9px] font-bold`}>
                                                    {t(c.key).substring(0, 2)}
                                                </span>
                                            </th>
                                        ))}
                                        <th className="number text-center" style={{ width: '30px' }} title={t('total')}>
                                            <IconUsers size={12} className="inline" />
                                        </th>
                                        {uniqueAttrs.map(attr => (
                                            <th className='py-2 px-2' key={attr.key} >
                                                <div className="flex gap-1">
                                                    {t(attr.key?.split('#')?.[0] || attr.key)}
                                                    {attr.key.split('#')?.[1] && (
                                                        <span className="font-bold">
                                                            #{attr.key.split('#')?.[1]}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                        <th >{t('group')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {sortedBookings.map((booking) => {
                                        const clientName = booking.reservation?.client_business?.name || '';
                                        return (
                                            <tr
                                                key={booking.id}
                                                className="hover:bg-gray-50/50 transition-colors"
                                            >
                                                <td className='py-2 px-2'>
                                                    <div className="font-bold whitespace-nowrap text-xs">
                                                        {booking.start_hour} {booking.end_hour ? `- ${booking.end_hour}` : ''}
                                                    </div>
                                                </td>

                                                <td className='py-2 px-2'>
                                                    {(booking.service_order_pax_groups && booking.service_order_pax_groups.length > 0) ? (
                                                        booking.service_order_pax_groups.map((sog: ServiceOrderPaxGroup) => {
                                                            const groupName = sog.pax_group?.name?.trim() || '';
                                                            const persons = sortPersons(sog.pax_group?.persons || []);
                                                            const effectiveShowPaxList = showPaxList || !groupName || persons.length <= 1;
                                                            const allocatedCount = BookingPaxsSize(sog);
                                                            const visiblePersons = !effectiveShowPaxList ? [] : (onlyFirstPax ? persons.slice(0, 1) : persons);
                                                            const showGroupSize =
                                                                visiblePersons.length !== 1 ||
                                                                persons.length !== 1 ||
                                                                allocatedCount !== BookingPaxsSize(visiblePersons[0]);
                                                            const showGroupHeader = Boolean(groupName) || showGroupSize || Boolean(sog.notes);

                                                            return (
                                                                <div key={sog.id || sog.pax_group_id} className="text-xs space-y-0.5">
                                                                    {showGroupHeader && (
                                                                        <div className="flex flex-wrap font-medium items-center gap-1">
                                                                            {(Boolean(groupName) || showGroupSize) && (
                                                                                <IconUsersGroup size={11} className="shrink-0 text-indigo-500" />
                                                                            )}
                                                                            {groupName && (
                                                                                <div className="font-bold text-slate-800">
                                                                                    {groupName}
                                                                                </div>
                                                                            )}
                                                                            {showGroupSize && (
                                                                                <div className="font-extrabold text-indigo-700">(x{allocatedCount})</div>
                                                                            )}
                                                                            {sog.notes && (
                                                                                <span className="text-[10px] text-slate-500 italic ml-1">
                                                                                    ({sog.notes})
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {visiblePersons.length > 0 && (
                                                                        <div className={`${showGroupHeader ? 'pl-3' : ''} space-y-0.5 text-slate-700`}>
                                                                            {visiblePersons.map((p: Person) => (
                                                                                <div key={p.id} className="py-0.25">
                                                                                    <ShowPerson person={p} showPhone={showPhone} />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div className="text-xs flex gap-0.5 items-center font-bold text-gray-600">
                                                            <IconUsersGroup size={10} className="shrink-0 text-blue-400" />{t('group')} {clientName} <span className='font-light'>(#{booking.id})</span>
                                                        </div>
                                                    )}
                                                    {booking.observations && (
                                                        <div className="text-xxs px-1.5 py-0.5 bg-red-50 text-red-700 w-fit mt-1 rounded border border-red-100/55">
                                                            {booking.observations}
                                                        </div>
                                                    )}
                                                </td>
                                                {
                                                    categories.map(c => {
                                                        const allocated = booking[c.key] || 0;

                                                        if (allocated === 0) {
                                                            return <td key={c.key} className="number text-center py-1"></td>;
                                                        }

                                                        return (
                                                            <td key={c.key} className="number text-center font-light text-gray-600 text-xs py-1">
                                                                {allocated}
                                                            </td>
                                                        );
                                                    })
                                                }
                                                <td className="number text-center text-md">
                                                    <span className="font-extrabold">
                                                        {(booking.adults || 0) + (booking.children || 0) + (booking.seniors || 0) + (booking.guests || 0)}
                                                    </span>
                                                </td>
                                                {
                                                    uniqueAttrs.map(uAttr => {
                                                        const attr = booking.self_attrs?.find(a => a.key === uAttr.key);
                                                        if (!attr) return <td key={uAttr.key} ></td>;
                                                        return (
                                                            <td key={uAttr.key} className='py-2 px-2'>
                                                                <div className="font-medium text-xs break-words">
                                                                    {attr.type === 'date' && formatDate(attr.value)}
                                                                    {attr.type === 'boolean' && (attr.value ? t('yes') : t('no'))}
                                                                    {attr.type === 'number' && attr.value}
                                                                    {attr.type === 'text' && attr.value}
                                                                </div>
                                                            </td>
                                                        );
                                                    })
                                                }
                                                <td>
                                                    <div className="text-xs font-bold text-gray-900 break-words w-fit bg-gray-200 px-1 w-fit py-0.5 rounded-md">
                                                        {booking.reservation?.client_business?.name}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div >
                );
            })}
        </div >
    );
}
